import { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  fetchDashboardData,
  requestProjectAccess,
  updatePorjectAccessRequest,
} from "../../api/commonServices";
import { toast } from "react-toastify";
import {
  X,
  Trash2,
  Plus,
  FolderOpen,
  UserX,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useSelector } from "react-redux";
import { displayImage } from "../../lib/displayImage";
const Dashboard = () => {
  const { user } = useSelector((state) => state.user);
  const [isLoading, setisLoading] = useState(false);
  const [users, setusers] = useState([]);
  const [projects, setprojects] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [requesting, setrequesting] = useState({
    isLoading: false,
    projectId: null,
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [accessControls, setAccessControls] = useState({
    create: false,
    deleteOwn: false,
    deleteAny: false,
    execute: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setisLoading(true);
    try {
      const response = await fetchDashboardData();
      setusers(response?.users);
      setprojects(response?.projectsData);
      setAccessRequests(response?.requests || []);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setisLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getAvatarColor = (index) => {
    const colors = [
      "bg-blue-500",
      "bg-purple-500",
      "bg-green-500",
      "bg-orange-500",
      "bg-pink-500",
    ];
    return colors[index % colors.length];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Granted":
        return "text-green-600 bg-green-50";
      case "No Access":
        return "text-red-600 bg-red-50";
      case "Pending Approval":
        return "text-yellow-600 bg-yellow-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const sendRequest = async (projectId) => {
    setrequesting({ isLoading: true, projectId });
    try {
      await requestProjectAccess(projectId);
      await fetchData();
      toast.success("Project access request sent successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to send project access request",
      );
    } finally {
      setrequesting({ isLoading: false, projectId: null });
    }
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setAccessControls({
      create: false,
      deleteOwn: false,
      deleteAny: false,
      execute: true,
    });
  };

  const handleApprove = async () => {
    setApproveLoading(true);
    try {
      await updatePorjectAccessRequest(
        selectedRequest._id,
        "Approved",
        accessControls,
      );
      toast.success("Request approved successfully");
      setSelectedRequest(null);
      await fetchData();
    } catch (error) {
      toast.error(error || "Failed to approve request");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async () => {
    setRejectLoading(true);
    try {
      await updatePorjectAccessRequest(selectedRequest._id, "Rejected", {});
      toast.success("Request rejected successfully");
      setSelectedRequest(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to reject request");
    } finally {
      setRejectLoading(false);
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case "create":
        return "text-green-600 bg-green-50";
      case "deleteOwn":
        return "text-orange-600 bg-orange-50";
      case "deleteAny":
        return "text-red-600 bg-red-50";
      case "execute":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case "create":
        return <Plus size={14} />;
      case "deleteOwn":
        return <UserX size={14} />;
      case "deleteAny":
        return <Trash2 size={14} />;
      case "execute":
        return <FolderOpen size={14} />;
      default:
        return null;
    }
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case "create":
        return "Create";
      case "deleteOwn":
        return "Delete Own";
      case "deleteAny":
        return "Delete Any";
      case "execute":
        return "Execute";
      default:
        return permission;
    }
  };

  const has_requestComponent_permission = () => {
    if (
      user &&
      (user.role === "super_admin" ||
        user.permissions.includes("Onboarding Manager"))
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="w-full p-4 md:p-6 bg-gray-50 overflow-auto h-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 auto-rows-min">
        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col min-h-[300px] max-h-[600px] lg:max-h-[500px]">
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-300 text-black">
            <h2 className="text-xl font-semibold">Users</h2>
            <div className="flex items-center gap-1.5 bg-gray-800 text-white rounded-md px-3 py-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="text-sm font-semibold">{users?.length}</span>
            </div>
          </div>
          <div className="overflow-auto flex-1">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    User
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Permissions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="px-4 md:px-6 py-8 text-center text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="px-4 md:px-6 py-8 text-center text-gray-500"
                    >
                      No users found
                    </td>
                  </tr>
                ) : (
                  users?.map((user, index) => (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex items-center gap-3 md:gap-4 min-w-[200px]">
                          <div
                            className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${
                              !user?.profileImage
                                ? getAvatarColor(index)
                                : "bg-transparent"
                            } flex items-center justify-center text-white font-semibold text-sm md:text-lg flex-shrink-0`}
                          >
                            {user.profileImage?.data ? (
                              <img
                                src={displayImage(user.profileImage.data)}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="w-full h-full rounded-full object-cover "
                              />
                            ) : (
                              getInitials(user.firstName, user.lastName)
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 capitalize text-sm md:text-base truncate">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">
                              {user.email}
                            </div>
                            {user.designation && (
                              <div className="text-xs text-gray-500 mt-1 truncate">
                                {user.designation}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex flex-wrap gap-2 min-w-[150px]">
                          {user.role === "super_admin" ? (
                            <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                              Super Admin
                            </span>
                          ) : (
                            <>
                              {user.permissions &&
                              user.permissions.length > 0 ? (
                                <>
                                  {user.permissions.map((permission, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap"
                                    >
                                      {permission}
                                    </span>
                                  ))}
                                </>
                              ) : (
                                <span className="text-xs md:text-sm text-red-400 italic">
                                  No permissions assigned
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Projects Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col min-h-[300px] max-h-[600px] lg:max-h-[500px]">
          <div className="px-6 py-4 border-b bg-gray-300 text-black flex items-center justify-between">
            <h2 className="text-xl font-semibold">Projects</h2>
            <div className="flex items-center gap-1.5 bg-gray-800 text-white rounded-md px-3 py-1.5">
              {/* folder icon */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <span className="text-sm font-semibold">{projects.length}</span>
            </div>
          </div>
          <div className="overflow-auto flex-1">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Project Name
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Access
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Request Access
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-4 md:px-6 py-8 text-center text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-4 md:px-6 py-8 text-center text-gray-500"
                    >
                      No projects found
                    </td>
                  </tr>
                ) : (
                  projects?.map((project, index) => (
                    <tr
                      key={project._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 md:px-6 py-4">
                        <div className="font-medium text-gray-900 text-sm md:text-base">
                          {project.projectName}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div
                          className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.actionText)}`}
                        >
                          {project.actionText}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        {project.hasAccess ||
                        project.actionText === "Pending Approval" ? (
                          "-"
                        ) : (
                          <>
                            {requesting.isLoading &&
                            requesting.projectId === project._id ? (
                              <span className="text-sm text-gray-500 italic">
                                Sending Request...
                              </span>
                            ) : (
                              <button
                                className="px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 text-white text-xs md:text-sm rounded-md hover:bg-blue-700 transition"
                                onClick={() => sendRequest(project._id)}
                              >
                                Request Access
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Access Requests Table */}

        {has_requestComponent_permission() && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden flex flex-col  max-h-[600px]">
            <div className="px-6 py-4 border-b bg-gray-300 text-black flex items-center justify-between">
              <h2 className="text-xl font-semibold">Project Requests</h2>
              <div className="flex items-center gap-1.5 bg-gray-800 text-white rounded-md px-3 py-1.5">
                {/* inbox / tray icon */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                </svg>
                <span className="text-sm font-semibold">
                  {accessRequests.length}
                </span>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      User Name
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Email
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Requested Project
                    </th>
                    <th className="px-4 md:px-6 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 md:px-6 py-8 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : accessRequests.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 md:px-6 py-8 text-center text-gray-500 "
                      >
                        No requests found
                      </td>
                    </tr>
                  ) : (
                    accessRequests?.map((request, index) => (
                      <tr
                        key={request._id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleRequestClick(request)}
                      >
                        <td className="px-4 md:px-6 py-4">
                          <div className="font-medium text-gray-900 text-sm md:text-base">
                            {request.requestedBy.firstName}{" "}
                            {request.requestedBy.lastName}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="text-sm md:text-base text-gray-700">
                            {request.requestedBy.email}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <div className="font-medium text-gray-900 text-sm md:text-base">
                            {request.projectId.projectName}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-4">
                          <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {request.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Request Approval Dialog */}
      <Dialog
        open={!!selectedRequest}
        as="div"
        className="relative z-50 focus:outline-none"
        onClose={() => setSelectedRequest(null)}
      >
        <div className="fixed inset-0 z-50 w-screen overflow-y-auto bg-black bg-opacity-70 font-raleway">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white backdrop-blur-2xl shadow-2xl">
              <div className="relative">
                <DialogTitle
                  as="h3"
                  className="text-xl font-bold text-gray-800 border-b border-gray-200 p-6 pr-16"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FolderOpen className="text-blue-600" size={20} />
                    </div>
                    Review Access Request
                  </div>
                </DialogTitle>
                <button
                  type="button"
                  onClick={() => setSelectedRequest(null)}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Close"
                  disabled={approveLoading || rejectLoading}
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>

              {selectedRequest && (
                <div className="p-6 space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900 mb-5">
                      Request Details
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* User Info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-black font-semibold mb-3">
                          User Information
                        </p>

                        <div className="space-y-3">
                          <div>
                            <span className="text-sm text-black font-semibold ">
                              Name
                            </span>
                            <p className="text-gray-900 font-medium">
                              {selectedRequest.requestedBy.firstName}{" "}
                              {selectedRequest.requestedBy.lastName}
                            </p>
                          </div>

                          <div>
                            <span className="text-sm text-black font-semibold ">
                              Email
                            </span>
                            <p className="text-gray-900 break-all">
                              {selectedRequest.requestedBy.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Project Info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-black font-semibold  mb-3">
                          Project Information
                        </p>

                        <div className="space-y-3">
                          <div>
                            <span className="text-sm text-black font-semibold ">
                              Project Name
                            </span>
                            <p className="text-gray-900 font-medium">
                              {selectedRequest.projectId.projectName}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Access Controls */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FolderOpen size={18} className="text-green-600" />
                      Select Access Controls
                    </h4>

                    <div className="flex flex-wrap gap-3">
                      {["create", "deleteOwn", "deleteAny", "execute"].map(
                        (perm) => (
                          <label
                            key={perm}
                            className={`flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                              accessControls[perm]
                                ? getPermissionColor(perm) + " border-current"
                                : "text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={accessControls[perm]}
                              onChange={(e) =>
                                setAccessControls({
                                  ...accessControls,
                                  [perm]: e.target.checked,
                                })
                              }
                              className="sr-only"
                            />
                            <div
                              className={`flex items-center gap-1 ${
                                accessControls[perm] ? "font-medium" : ""
                              }`}
                            >
                              {getPermissionIcon(perm)}
                              <span>{getPermissionLabel(perm)}</span>
                            </div>
                            {accessControls[perm] && (
                              <div className="w-2 h-2 bg-current rounded-full"></div>
                            )}
                          </label>
                        ),
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleReject}
                      disabled={approveLoading || rejectLoading}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all inline-flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {rejectLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          Reject Request
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={approveLoading || rejectLoading}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all inline-flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {approveLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Approving...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle size={16} />
                          Approve Request
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Dashboard;
